import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { APP_CONFIG } from '@/config/app-config';

const formSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
});

type FormValues = z.infer<typeof formSchema>;

const ForgotPassword: React.FC = () => {
  const { resetPassword } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  
  // Get user type from URL params, default to client
  const userType = searchParams.get('type') === 'practitioner' ? 'practitioner' : 'client';

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      console.log('Sending password reset email for:', userType, 'domain:', APP_CONFIG.PRODUCTION_DOMAIN);
      
      const { error } = await resetPassword(values.email, userType);
      
      if (error) {
        throw new Error(error.message);
      }
      
      toast({
        title: 'Reset Email Sent',
        description: 'Please check your email for password reset instructions.',
        duration: 8000,
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to send reset email. Please try again.',
        duration: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  const getBackToLoginPath = () => {
    return userType === 'practitioner' ? '/practitioner-auth' : '/client-auth';
  };

  const getLoginPageTitle = () => {
    return userType === 'practitioner' ? 'Practitioner Login' : 'Client Login';
  };

  return (
    <div className="min-h-screen bg-amber-50">
      <Navbar />
      <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-left mb-8">
            <h1 className="text-2xl font-semibold text-neutral-800">
              Forgot Your Password?
            </h1>
            <p className="text-neutral-600">
              Enter your email address and we'll send you a link to reset your {userType} account password
            </p>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-xl font-semibold text-amber-800">
              Reset Password
            </h2>
            <p className="mb-6 text-sm text-neutral-600">
              We'll send password reset instructions to your email address
            </p>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    className="border-neutral-300 bg-neutral-50 pl-10"
                    disabled={loading}
                    {...form.register('email')}
                  />
                  <span className="absolute left-3 top-2.5 text-neutral-500">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </span>
                </div>
                {form.formState.errors.email && (
                  <p className="text-sm text-red-600 mt-1">{form.formState.errors.email.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="mt-2 w-full bg-amber-600 hover:bg-amber-700"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  'Send Reset Email'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-neutral-600">
              Remember your password?{' '}
              <Link 
                to={getBackToLoginPath()} 
                className="text-amber-600 hover:text-amber-800 hover:underline"
              >
                Back to {getLoginPageTitle()}
              </Link>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-neutral-600">
            Didn't receive an email? Check your spam folder or try again in a few minutes.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
